import { useMemo, useState, useEffect } from "react";
import { useTranslations } from 'use-intl';
import { Loader2 } from 'lucide-react';
import FinancialSummary from '@/components/dashboard/FinancialSummary';
import BankAccountsList from '@/components/dashboard/BankAccountsList';
import CurrencyAccountsList from '@/components/dashboard/CurrencyAccountsList';
import PageHeaderActions from '@/components/common/PageHeaderActions';
import { useFinancialBalances } from '@/hooks/useFinancialBalances';
import { useOverviewData } from '@/hooks/useOverviewData';
import PayablesTableSection from '@/components/payments/PayablesTableSection';
import usePayablesData from '@/hooks/usePayablesData';
import { useOutletContext } from 'react-router-dom';
import { useBankAccounts } from "@/hooks/useBankAccounts";
import { useCompanyOnboarding } from "@/hooks/useCompanyOnboarding";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";
import { SetupModal } from "@/components/onboarding/SetupModal";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";

const Overview = () => {
  const t = useTranslations('consultantOnboarding');
  const { sidebarCollapsed } = useOutletContext<{ sidebarCollapsed: boolean }>();
  
  // Connected bank accounts for onboarding
  const { accounts: connectedAccounts } = useBankAccounts();
  
  // Company onboarding flow (ERP, bank integration, etc.)
  const {
    onboardingCompleted,
    erpConnectionCompleted,
    bankConnectionCompleted,
    isLoading: companyOnboardingLoading,
    isStatusLoading,
    isInitialLoadComplete,
    checkOnboardingStatus,
    isCompanyOnboardingComplete
  } = useCompanyOnboarding();

  // Show modals based on company onboarding status
  // WelcomeModal: Show only when onboarding is not completed and initial load is complete
  const showWelcomeModal = isInitialLoadComplete && !onboardingCompleted;

  // SetupModal: Show when either ERP or bank connection is not completed, but only if welcome modal is not showing
  // Wait for initial load to complete to avoid showing intermediate loading states in the modal
  const showSetupModal = isInitialLoadComplete && !showWelcomeModal && (!erpConnectionCompleted || !bankConnectionCompleted);

  const handleContinueToSetup = () => {
    // Close welcome modal and show setup modal
    setWelcomeModalOpen(false);
    // The useEffect will handle showing the setup modal if needed
  };

  const handleCloseSetup = () => {
    // This will be handled by the SetupModal
  };

  const handleSkipERP = async () => {
    // This will be handled by the SetupModal
  };

  // Add state to control modal visibility
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(showWelcomeModal);
  const [setupModalOpen, setSetupModalOpen] = useState(showSetupModal);

  // Update modal states when onboarding status changes
  useEffect(() => {
    setWelcomeModalOpen(showWelcomeModal);
    setSetupModalOpen(showSetupModal);
  }, [showWelcomeModal, showSetupModal]);

  // Close modals if initial load is not complete (prevents showing loading state in modals)
  useEffect(() => {
    if (!isInitialLoadComplete) {
      setWelcomeModalOpen(false);
      setSetupModalOpen(false);
    }
  }, [isInitialLoadComplete]);

  // Auto-show setup modal when welcome modal is closed and there are incomplete items
  useEffect(() => {
    if (!welcomeModalOpen && isInitialLoadComplete && !onboardingCompleted && (!erpConnectionCompleted || !bankConnectionCompleted)) {
      setSetupModalOpen(true);
    }
  }, [welcomeModalOpen, isInitialLoadComplete, onboardingCompleted, erpConnectionCompleted, bankConnectionCompleted]);

  // Auto-close setup modal when all onboarding is complete
  useEffect(() => {
    if (erpConnectionCompleted && bankConnectionCompleted) {
      setSetupModalOpen(false);
    }
  }, [erpConnectionCompleted, bankConnectionCompleted]);

  const handleCloseWelcome = () => {
    setWelcomeModalOpen(false);
    // The useEffect will handle showing the setup modal if needed
  };

  const handleCloseSetupModal = () => {
    setSetupModalOpen(false);
    // Refresh onboarding status to check if anything changed
    checkOnboardingStatus();
    // If there are still incomplete items, we might want to show the modal again later
    // The user can manually trigger it via the indicator button
  };
  
  // Get company name from first account or fallback
  const { company } = useCompany();
  const companyName = company?.name || t('app.loading');
  // Load financial balances
  const {
    bankBalance,
    payablesBalance,
    receivablesBalance,
    loadingBankBalance,
    loadingPayablesBalance,
    loadingReceivablesBalance,
  } = useFinancialBalances('SEK');

  // Load bank accounts (only)
  const {
    bankAccounts,
    loadingAccounts,
  } = useOverviewData();

  // Use shared payables data hook
  const {
    payables,
    setPayables,
    loading: loadingPayables,
    refreshing,
    refreshData,
  } = usePayablesData();

  // Aggregate currency accounts from bank accounts
  const currencyAccounts = useMemo(() => {
    const currencyMap: Record<string, number> = {};
    bankAccounts.forEach(account => {
      if (!currencyMap[account.currency]) {
        currencyMap[account.currency] = 0;
      }
      currencyMap[account.currency] += account.balance;
    });
    return Object.entries(currencyMap).map(([currency, amount], index) => {
      let symbol = '$';
      switch(currency) {
        case 'EUR': symbol = '€'; break;
        case 'GBP': symbol = '£'; break;
        case 'JPY': symbol = '¥'; break;
        case 'SEK': symbol = 'kr'; break;
        default: symbol = '$';
      }
      return {
        id: (index + 1).toString(),
        currency,
        amount,
        symbol
      };
    });
  }, [bankAccounts]);

  let setupIndicator = t('overview.bankPending');
  if (!erpConnectionCompleted && !bankConnectionCompleted) {
    setupIndicator = t('overview.setupNeeded');
  } else if (!erpConnectionCompleted) {
    setupIndicator = t('overview.erpPending');
  }

  // Financial data for summary
  const financialData = {
    bankBalance: {
      value: loadingBankBalance ? t('app.loading') : bankBalance,
      trend: {
        value: 12,
        positive: true
      }
    },
    receivables: {
      value: loadingReceivablesBalance ? t('app.loading') : receivablesBalance,
      trend: {
        value: 8,
        positive: true
      }
    },
    payables: {
      value: loadingPayablesBalance ? t('app.loading') : payablesBalance,
      trend: {
        value: 5,
        positive: false
      }
    }
  };

  return (
    <div className="space-y-6 overflow-x-hidden box-border">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h1 className="text-3xl font-bold tracking-tight">{t('overview.title')}</h1>
        <div className="flex items-center gap-4">
          {/* Show onboarding indicator if there are incomplete items */}
          {isInitialLoadComplete && (!erpConnectionCompleted || !bankConnectionCompleted) && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="text-sm text-amber-800">
                {setupIndicator}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSetupModalOpen(true)}
                  className="text-amber-700 border-amber-300 hover:bg-amber-100"
                  disabled={!isInitialLoadComplete || isStatusLoading}
                >
                  {!isInitialLoadComplete || isStatusLoading ? t('app.loading') : t('overview.completeSetup')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={checkOnboardingStatus}
                  className="text-amber-600 hover:bg-amber-100"
                  disabled={!isInitialLoadComplete || isStatusLoading}
                >
                  <Loader2 className={`h-4 w-4 ${!isInitialLoadComplete || isStatusLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          )}


          <PageHeaderActions onRefresh={refreshData} refreshing={refreshing} />
        </div>
      </div>

      {/* Responsive grid for the three main cards */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 max-w-screen-2xl mx-auto">
        <FinancialSummary financialData={financialData} />
        {loadingAccounts ? (
          <div className="flex items-center justify-center h-full border rounded-lg p-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
            <span>{t('overview.loadingAccounts')}</span>
          </div>
        ) : (
          <BankAccountsList bankAccounts={bankAccounts} />
        )}
        <CurrencyAccountsList currencyAccounts={currencyAccounts} />
      </div>

      {/* Unpaid Payables Section */}
      {loadingPayables ? (
        <div className="flex items-center justify-center h-32 border rounded-lg p-8 mt-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
          <span>{t('overview.loadingPayables')}</span>
        </div>
      ) : (
        <PayablesTableSection 
          invoices={payables}
          setInvoices={setPayables}
          showTabs={false}
          statusFilter={['unpaid','overdue']}
          refreshData={refreshData}
        />
      )}

      {/* Onboarding Modals */}
      <WelcomeModal
        isOpen={welcomeModalOpen}
        companyName={companyName}
        onContinueToSetup={handleContinueToSetup}
        onClose={handleCloseWelcome}
      />
      
      <SetupModal
        isOpen={setupModalOpen}
        erpConnectionCompleted={erpConnectionCompleted}
        bankConnectionCompleted={bankConnectionCompleted}
        isLoading={isStatusLoading}
        onClose={handleCloseSetupModal}
        onSkipERP={handleSkipERP}
      />
    </div>
  );
};

export default Overview;
